'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCustomAddressStore } from '@libs/common-next';
import type { Product, TailoredApiData } from '@libs/common-types';
import { type DeliveryInventoryStatus, getBopisFulfillmentStatus, getDeliveryInventoryStatus, getSddFulfillmentStatus, getSthFulfillmentStatus, resolveFulfillmentMethod, type SddDetailsProps } from '../../product-details';
import type { MiniPdpForm } from '../types';
export type MiniPdpFulfillmentProps = Readonly<{
  product: Product;
  tailored: TailoredApiData | null;
  isLoading: boolean;
  isError?: boolean;
  onOpenSddAddress?: () => void;
  onOpenStoreDrawer: () => void;
}>;
export function useMiniPdpFulfillment(t0) {
  const $ = _c(75);
  const {
    product,
    tailored,
    isLoading,
    isError: t1,
    onOpenSddAddress
  } = t0;
  const isError = t1 === undefined ? false : t1;
  const {
    register,
    setValue
  } = useFormContext();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      name: "fulfilmentMethod"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const selectedMethod = useWatch(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      name: "selectedSku"
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const selectedSku = useWatch(t3);
  const customAddress = useCustomAddressStore(_temp);
  const tailoredVariant = selectedSku ? tailored?.variants[selectedSku] : undefined;
  const bopis = tailoredVariant?.BOPIS;
  const sdd = tailoredVariant?.SDD;
  const sth = tailoredVariant?.STH;
  const isBopisAvailableForPurchase = product.deliveryMethods.BOPIS.availablePurchaseOptions.oneTime;
  const isBopisAvailableForStore = bopis?.storeId ? tailored?.bopisStoreData?.[bopis.storeId]?.offersBopis ?? true : true;
  const isBopisAvailable = isBopisAvailableForPurchase && isBopisAvailableForStore;
  const isSddAvailable = product.deliveryMethods.SDD.availablePurchaseOptions.oneTime;
  const isSthAvailable = product.deliveryMethods.STH.availablePurchaseOptions.oneTime;
  let t4;
  if ($[2] !== isBopisAvailable || $[3] !== isSddAvailable || $[4] !== isSthAvailable) {
    t4 = {
      bopis: isBopisAvailable,
      sdd: isSddAvailable,
      sth: isSthAvailable
    };
    $[2] = isBopisAvailable;
    $[3] = isSddAvailable;
    $[4] = isSthAvailable;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const availableMethods = t4;
  let t5;
  if ($[6] !== availableMethods || $[7] !== isLoading || $[8] !== selectedMethod || $[9] !== setValue) {
    t5 = () => {
      if (isLoading) {
        return;
      }
      const resolvedMethod = resolveFulfillmentMethod({
        availableMethods,
        selectedMethod
      });
      if (resolvedMethod !== selectedMethod) {
        setValue("fulfilmentMethod", resolvedMethod);
      }
    };
    $[6] = availableMethods;
    $[7] = isLoading;
    $[8] = selectedMethod;
    $[9] = setValue;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== isBopisAvailable || $[12] !== isLoading || $[13] !== isSddAvailable || $[14] !== isSthAvailable || $[15] !== selectedMethod || $[16] !== setValue) {
    t6 = [isLoading, selectedMethod, setValue, isBopisAvailable, isSddAvailable, isSthAvailable];
    $[11] = isBopisAvailable;
    $[12] = isLoading;
    $[13] = isSddAvailable;
    $[14] = isSthAvailable;
    $[15] = selectedMethod;
    $[16] = setValue;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  useEffect(t5, t6);
  const t7 = bopis?.availableStock ?? 0;
  const t8 = bopis?.altAvailableStock ?? 0;
  const t9 = product.deliveryMethods.BOPIS.purchaseQuantities?.min ?? 1;
  const t10 = Boolean(bopis?.storeId);
  const t11 = Boolean(product.pharmacy);
  let t12;
  if ($[18] !== t10 || $[19] !== t11 || $[20] !== t7 || $[21] !== t8 || $[22] !== t9) {
    t12 = getBopisFulfillmentStatus({
      availableStock: t7,
      altAvailableStock: t8,
      minimumCaseQty: t9,
      isStoreSelected: t10,
      isPharmacy: t11
    });
    $[18] = t10;
    $[19] = t11;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const t13 = selectedMethod === "bopis" && !isLoading && isBopisAvailable;
  const t14 = !isLoading && !isBopisAvailable;
  let t15;
  if ($[24] !== isLoading || $[25] !== t12 || $[26] !== t13 || $[27] !== t14) {
    t15 = {
      value: "bopis" as const,
      title: "Pick up in store",
      iconName: "store-outline" as const,
      statusTestId: "bopis-tile-status-message",
      status: t12,
      isSelected: t13,
      isDisabled: t14,
      isLoading
    };
    $[24] = isLoading;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = sdd?.availableStock ?? 0;
  const t17 = product.deliveryMethods.SDD.purchaseQuantities?.min ?? 1;
  const t18 = Boolean(sdd?.doorDashValidationSuccess);
  const t19 = Boolean(customAddress?.streetLine1);
  let t20;
  if ($[29] !== t16 || $[30] !== t17 || $[31] !== t18 || $[32] !== t19) {
    t20 = getSddFulfillmentStatus({
      availableStock: t16,
      minimumCaseQty: t17,
      isAvailable: t18,
      hasStreetAddress: t19
    });
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  const t21 = selectedMethod === "sdd" && !isLoading && isSddAvailable;
  const t22 = !isLoading && !isSddAvailable;
  let t23;
  if ($[34] !== isLoading || $[35] !== t20 || $[36] !== t21 || $[37] !== t22) {
    t23 = {
      value: "sdd" as const,
      title: "Same-day delivery",
      iconName: "box-outline" as const,
      statusTestId: "sdd-tile-status-message",
      status: t20,
      isSelected: t21,
      isDisabled: t22,
      isLoading
    };
    $[34] = isLoading;
    $[35] = t20;
    $[36] = t21;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const t24 = sth?.availableStock ?? 0;
  const t25 = product.deliveryMethods.STH.purchaseQuantities?.min ?? 1;
  const t26 = Boolean(product.pharmacy?.oos);
  let t27;
  if ($[39] !== t24 || $[40] !== t25 || $[41] !== t26) {
    t27 = getSthFulfillmentStatus({
      availableStock: t24,
      minimumCaseQty: t25,
      isPharmacyOutOfStock: t26
    });
    $[39] = t24;
    $[40] = t25;
    $[41] = t26;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  const t28 = selectedMethod === "sth" && !isLoading && isSthAvailable;
  const t29 = !isLoading && !isSthAvailable;
  let t30;
  if ($[43] !== isLoading || $[44] !== t27 || $[45] !== t28 || $[46] !== t29) {
    t30 = {
      value: "sth" as const,
      title: "Ship to me",
      iconName: "car-outline" as const,
      statusTestId: "stm-tile-status-message",
      status: t27,
      isSelected: t28,
      isDisabled: t29,
      isLoading
    };
    $[43] = isLoading;
    $[44] = t27;
    $[45] = t28;
    $[46] = t29;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== t15 || $[49] !== t23 || $[50] !== t30) {
    t31 = [t15, t23, t30];
    $[48] = t15;
    $[49] = t23;
    $[50] = t30;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  const fulfillmentMethods = t31;
  let t32;
  if ($[52] !== customAddress?.streetLine1 || $[53] !== isError || $[54] !== isLoading || $[55] !== onOpenSddAddress || $[56] !== product.deliveryMethods.SDD.purchaseQuantities?.min || $[57] !== sdd?.availableStock || $[58] !== sdd?.doorDashValidationSuccess || $[59] !== selectedMethod) {
    t32 = selectedMethod === "sdd" && !isError ? {
      isLoading,
      streetAddress: customAddress?.streetLine1,
      availableStock: sdd?.availableStock ?? 0,
      minimumCaseQty: product.deliveryMethods.SDD.purchaseQuantities?.min ?? 1,
      isDoorDashAvailable: Boolean(sdd?.doorDashValidationSuccess),
      onOpenAddressDrawer: onOpenSddAddress
    } : null;
    $[52] = customAddress?.streetLine1;
    $[53] = isError;
    $[54] = isLoading;
    $[55] = onOpenSddAddress;
    $[56] = product.deliveryMethods.SDD.purchaseQuantities?.min;
    $[57] = sdd?.availableStock;
    $[58] = sdd?.doorDashValidationSuccess;
    $[59] = selectedMethod;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  const sddDetails = t32;
  let t33;
  if ($[61] !== isError || $[62] !== isLoading || $[63] !== product.deliveryMethods.STH.purchaseQuantities?.min || $[64] !== product.pharmacy?.oos || $[65] !== selectedMethod || $[66] !== sth?.availableStock) {
    t33 = selectedMethod === "sth" && !isError ? getDeliveryInventoryStatus({
      method: "sth",
      isLoading,
      availableStock: sth?.availableStock,
      minimumCaseQty: product.deliveryMethods.STH.purchaseQuantities?.min ?? 1,
      customPharmacyOOS: Boolean(product.pharmacy?.oos)
    }) : null;
    $[61] = isError;
    $[62] = isLoading;
    $[63] = product.deliveryMethods.STH.purchaseQuantities?.min;
    $[64] = product.pharmacy?.oos;
    $[65] = selectedMethod;
    $[66] = sth?.availableStock;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  const inventoryStatus = t33;
  let t34;
  if ($[68] !== register) {
    t34 = register("fulfilmentMethod");
    $[68] = register;
    $[69] = t34;
  } else {
    t34 = $[69];
  }
  let t35;
  if ($[70] !== fulfillmentMethods || $[71] !== inventoryStatus || $[72] !== sddDetails || $[73] !== t34) {
    t35 = {
      fulfillmentMethods,
      register: t34,
      sddDetails,
      inventoryStatus
    };
    $[70] = fulfillmentMethods;
    $[71] = inventoryStatus;
    $[72] = sddDetails;
    $[73] = t34;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  return t35;
}
function _temp(state) {
  return state.address;
}